import React, { useMemo } from 'react'
import { MultiSelectFilter } from './multi-select-filter'
import { useEventRegRefreshContext } from '../reducers/event-reg-refresh-reducers'
import { IEpiProps, ISpecialtyProgram } from '../models/event-reg-refresh-models'

interface ProgramFiltersProps {
  programs: ISpecialtyProgram[]
  selectedFilters: {
    industries: string[]
    programFormats: string[]
    trainingTypes: string[]
  }
  onIndustryChange: (value: string, isChecked: boolean) => void
  onProgramFormatChange: (value: string, isChecked: boolean) => void
  onTrainingTypeChange: (value: string, isChecked: boolean) => void
  onApplyFilters: () => void
  showFilters: boolean
}

interface IContextProps {
  epiProps: IEpiProps
}

export const ProgramFilters: React.FC<ProgramFiltersProps> = ({
  programs,
  selectedFilters,
  onIndustryChange,
  onProgramFormatChange,
  onTrainingTypeChange,
  onApplyFilters,
  showFilters,
}) => {
  const { epiProps }: IContextProps = useEventRegRefreshContext()!

  // Extract unique filter values from programs
  const availableFilters = useMemo(() => {
    const industries = new Set<string>()
    const programFormats = new Set<string>()
    const trainingTypes = new Set<string>()

    programs.forEach(program => {
      if (program.industries) {
        program.industries.forEach(ind => industries.add(ind))
      }
      if (program.programFormats) {
        program.programFormats.forEach(pf => programFormats.add(pf))
      }
      if (program.trainingTypes) {
        program.trainingTypes.forEach(tt => trainingTypes.add(tt))
      }
    })

    return {
      industries: Array.from(industries).sort(),
      programFormats: Array.from(programFormats).sort(),
      trainingTypes: Array.from(trainingTypes).sort()
    }
  }, [programs])

  // Hide filters if:
  // 1. CMS setting hides all filters
  // 2. showFilters is false (when in specialty view or direct delivery view)
  if (epiProps.hideAllProgramFilters || !showFilters) {
    return null
  }

  // Check if any filters are visible
  const hasVisibleFilters =
    (availableFilters.industries.length > 0 && !epiProps.hideIndustryFilter) ||
    (availableFilters.programFormats.length > 0 && !epiProps.hideProgramFormatFilter) ||
    (availableFilters.trainingTypes.length > 0 && !epiProps.hideTrainingTypeFilter)

  // Don't render if no filters are visible
  if (!hasVisibleFilters) {
    return null
  }

  return (
    <div className="filters-inner">
        {availableFilters.industries.length > 0 && !epiProps.hideIndustryFilter && (
          <MultiSelectFilter
            title={epiProps.industryFilterLabel}
            options={availableFilters.industries}
            selectedValues={selectedFilters.industries}
            onSelectionChange={onIndustryChange}
          />
        )}

        {availableFilters.programFormats.length > 0 && !epiProps.hideProgramFormatFilter && (
          <MultiSelectFilter
            title={epiProps.programFormatFilterLabel}
            options={availableFilters.programFormats}
            selectedValues={selectedFilters.programFormats}
            onSelectionChange={onProgramFormatChange}
          />
        )}

        {availableFilters.trainingTypes.length > 0 && !epiProps.hideTrainingTypeFilter && (
          <MultiSelectFilter
            title={epiProps.trainingTypeFilterLabel}
            options={availableFilters.trainingTypes}
            selectedValues={selectedFilters.trainingTypes}
            onSelectionChange={onTrainingTypeChange}
          />
        )}

        <button
          className="btn-primary filters-cta"
          onClick={onApplyFilters}
        >
          {epiProps.applyFiltersButtonTextPrograms}
        </button>
    </div>
  )
}